import { ICoreNavigation } from '@feast-it/ui/light';

export type NavigationMetadata = {
  header: string | null;
  subheader: string | null;
  type: string;
};

/**
 * Builds a map of href -> navigation metadata from the navigation fixture
 * This allows us to look up which section/header a link belongs to for tracking
 */
export const buildNavigationMetadataMap = (
  navigation: ICoreNavigation
): Map<string, NavigationMetadata> => {
  const metadataMap = new Map<string, NavigationMetadata>();

  // Track button links
  if (navigation.buttonLinks) {
    if (navigation.buttonLinks.login?.href) {
      metadataMap.set(navigation.buttonLinks.login.href, {
        header: null,
        subheader: null,
        type: 'Login',
      });
    }
    if (navigation.buttonLinks.enquiry?.href) {
      metadataMap.set(navigation.buttonLinks.enquiry.href, {
        header: null,
        subheader: null,
        type: 'Get quotes',
      });
    }
  }

  // Track desktop navigation groups
  navigation.desktopNavGroups?.forEach(group => {
    let header: string | null = null;
    if (group.type === 'MEGA_MENU' && 'heading' in group && group.sections) {
      header = group.heading;
      group.sections.forEach(section => {
        const subheader = section.heading?.title || null;

        // Track section heading link if it exists
        if (section.heading?.link?.href && section.heading?.link?.children) {
          metadataMap.set(section.heading.link.href, {
            header,
            subheader,
            type: section.heading?.link?.children as string,
          });
        }

        // Track all links in the section
        section.links?.forEach(link => {
          if (link.href && link.children) {
            metadataMap.set(link.href, {
              header,
              subheader,
              type: link.children as string,
            });
          }
        });
      });
    } else if (group.type === 'POPOVER' && 'heading' in group && group.links) {
      header = group.heading;
      group.links.forEach(link => {
        if (link.href && link.children) {
          metadataMap.set(link.href, {
            header,
            subheader: null,
            type: link.children as string,
          });
        }
      });
    }
  });

  // Track mobile navigation groups
  navigation.mobileNavGroups?.forEach(group => {
    const header = 'heading' in group ? group.heading : null;
    if (group.type === 'SUB_NAV_LINK_CATEGORIES' && group.sections) {
      group.sections.forEach(section => {
        const subheader = section.heading || null;

        section.links?.forEach(link => {
          if (link.href && link.children) {
            metadataMap.set(link.href, {
              header,
              subheader,
              type: link.children as string,
            });
          }
        });
      });
    } else if (group.type === 'SUB_NAV_LINK_LIST' && group.links) {
      group.links.forEach(link => {
        if (link.href && link.children) {
          metadataMap.set(link.href, {
            header,
            subheader: null,
            type: link.children as string,
          });
        }
      });
    } else if (
      group.type === 'LINK' &&
      group.link?.href &&
      group.link?.children
    ) {
      metadataMap.set(group.link.href, {
        header,
        subheader: null,
        type: group.link.children as string,
      });
    }
  });

  return metadataMap;
};

/**
 * Gets navigation metadata for a given href
 * Returns default values if not found in the map
 */
export const getNavigationMetadata = (
  href: string,
  metadataMap: Map<string, NavigationMetadata>
): NavigationMetadata => {
  // Try exact match first
  if (metadataMap.has(href)) {
    return metadataMap.get(href)!;
  }

  // Try matching without query params
  const hrefWithoutQuery = href.split('?')[0];
  if (metadataMap.has(hrefWithoutQuery)) {
    return metadataMap.get(hrefWithoutQuery)!;
  }

  // Try matching pathname only (without domain)
  const pathname = hrefWithoutQuery.replace(/^https?:\/\/[^/]+/, '');
  if (metadataMap.has(pathname)) {
    return metadataMap.get(pathname)!;
  }

  // Return default values if no match found
  return {
    header: 'Unknown',
    subheader: null,
    type: 'unknown',
  };
};
